import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Stack, Typography } from '@mui/material'
import dayjs from 'dayjs'
import LocationCityIcon from '@mui/icons-material/LocationCity'
import CreditCardIcon from '@mui/icons-material/CreditCard'

import { useGetPatientQueueByStaffQuery } from '@/core/services/patient-queue'
import { isPatientInQueue } from '@/features/patient/patient-queue'

import { usePatientsCardView, PatientData } from '../model/usePatientsCardView'

export type PatientsTableProps = {
  autoHightlight?: boolean
  data: PatientData[]
  disableSelectRow?: boolean
  loading?: boolean
  onSelect?: (data: PatientData) => void
}

const PatientsCardView = (props: PatientsTableProps) => {
  const { data, disableSelectRow = false, loading, onSelect } = props
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { data: queueData } = useGetPatientQueueByStaffQuery()

  const { selectedRow, selectRow, addToQueue } = usePatientsCardView({ disableSelectRow, onSelect })

  const handleSelect = (rowData: PatientData) => () => {
    selectRow(rowData)
  }

  return (
    <Grid container spacing={2}>
      {loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <CircularProgress size={20} />
        </Box>
      )}
      {data.map((row) => (
        <Grid item xs={12} sm={6} md={4} key={row._id}>
          <Card
            onClick={handleSelect(row)}
            sx={{
              cursor: disableSelectRow ? 'default' : 'pointer',
              backgroundColor: selectedRow === row._id ? 'lightgray' : 'white',
              borderColor: selectedRow === row._id ? 'indigo' : undefined,
            }}
          >
            <CardContent>
              <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                {row.surname}
              </Typography>
              <Typography variant="body2" sx={{ color: 'gray.600' }}>
                {row.name}
              </Typography>
              <Grid item spacing={1} sx={{ mt: 1 }}>
                <Stack direction="row" spacing={1} alignItems="flex-end">
                  {row.gender && (
                    <Chip color={row.gender === 'M' ? 'primary' : row.gender === 'F' ? 'secondary' : 'info'} label={row.gender} size="small" variant="filled" />
                  )}
                  {row.birthday && <Chip color="info" label={dayjs(row.birthday).format('DD/MM/YYYY')} size="small" variant="filled" />}
                </Stack>
              </Grid>
              <Grid container direction="row" alignItems="center" spacing={1}>
                <Grid item>
                  <LocationCityIcon fontSize="small" color="primary" />
                </Grid>
                <Grid item>
                  <Typography>{row.city}</Typography>
                </Grid>
              </Grid>
              <Grid container direction="row" alignItems="center" spacing={1}>
                <Grid item>
                  <CreditCardIcon fontSize="small" color="primary" />
                </Grid>
                <Grid item>
                  <Typography>{row.secu}</Typography>
                </Grid>
              </Grid>
              {queueData && (
                <Grid item sx={{ mt: 1 }}>
                  <Stack direction="column" justifyContent="end" spacing={1}>
                    <Button variant="contained" color="primary" onClick={() => navigate(`/patients/${row._id}`)}>
                      {t('page.patients.seeProfileButton.tooltip')}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={isPatientInQueue(row._id, queueData)}
                      onClick={(e) => {
                        e.stopPropagation()
                        addToQueue(row._id)
                      }}
                    >
                      {isPatientInQueue(row._id, queueData) ? t('Patient already in queue') : t('Add to queue')}
                    </Button>
                  </Stack>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  )
}

export default PatientsCardView
